import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Send,
  CalendarDays,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import FadeIn from "@/components/FadeIn";
import { BRAND, DESTINATIONS_LIST, BUDGETS } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    budget: "",
    travelers: "",
    message: "",
  });
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [submitting, setSubmitting] = useState(false);

  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(
    "Hi DreamFly Travels, I'd like a quote."
  )}`;

  const onChange = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const travelDatesString = () => {
    if (!dateRange?.from) return "";
    if (!dateRange.to) return format(dateRange.from, "dd MMM yyyy");
    return `${format(dateRange.from, "dd MMM yyyy")} → ${format(
      dateRange.to,
      "dd MMM yyyy"
    )}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.destination) {
      toast.error("Please fill your name, email and destination.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        travel_dates: travelDatesString() || undefined,
      };
      await axios.post(`${API}/inquiries`, payload);
      toast.success(
        "Thank you! Your inquiry is in. A curator will reach out shortly."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        destination: "",
        budget: "",
        travelers: "",
        message: "",
      });
      setDateRange({ from: undefined, to: undefined });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Please try WhatsApp or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#F4EDE4]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: info panel */}
        <div className="lg:col-span-5">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#2C7A7B]">
              <span className="h-px w-8 bg-[#2C7A7B]" />
              Plan your escape
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <h2 className="mt-5 font-display text-[#0F3D3E] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Tell us your{" "}
              <span className="italic">dream trip</span>.
            </h2>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-6 text-[#0A0A0A]/75 leading-relaxed max-w-md">
              Share a few details and we'll craft a tailor-made proposal —
              usually within a few hours. Prefer to chat? WhatsApp is the
              fastest route to our team.
            </p>
          </FadeIn>

          <FadeIn delay={300}>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-big"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#2C7A7B] text-white px-6 py-4 text-base font-medium hover:bg-[#0F3D3E] transition-colors shadow-[0_20px_50px_-20px_rgba(44,122,123,0.7)]"
            >
              <MessageCircle size={20} />
              Chat on WhatsApp · {BRAND.whatsappDisplay}
            </a>
          </FadeIn>

          <FadeIn delay={400}>
            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <div className="size-11 rounded-full bg-white border border-[#0F3D3E]/10 flex items-center justify-center group-hover:bg-[#0F3D3E] group-hover:text-[#F4EDE4] transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#4B5563]">
                    Email
                  </div>
                  <div className="mt-0.5 text-[#0F3D3E] font-medium">
                    {BRAND.email}
                  </div>
                </div>
              </a>

              <a
                href={`tel:+${BRAND.whatsappNumber}`}
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <div className="size-11 rounded-full bg-white border border-[#0F3D3E]/10 flex items-center justify-center group-hover:bg-[#0F3D3E] group-hover:text-[#F4EDE4] transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#4B5563]">
                    Phone
                  </div>
                  <div className="mt-0.5 text-[#0F3D3E] font-medium">
                    {BRAND.phoneDisplay}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="size-11 rounded-full bg-white border border-[#0F3D3E]/10 flex items-center justify-center">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#4B5563]">
                    Based in
                  </div>
                  <div className="mt-0.5 text-[#0F3D3E] font-medium">
                    India · Serving travellers worldwide
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Right: form */}
        <FadeIn delay={200} className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="inquiry-form"
            className="rounded-3xl bg-white p-6 sm:p-10 card-shadow border border-[#0F3D3E]/8"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Full name
                </Label>
                <Input
                  id="name"
                  data-testid="form-name"
                  value={form.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  placeholder="Aarav Sharma"
                  className="bg-transparent border-[#0F3D3E]/15 focus-visible:ring-[#0F3D3E] focus-visible:border-[#0F3D3E] h-12 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="form-email"
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  placeholder="you@example.com"
                  className="bg-transparent border-[#0F3D3E]/15 focus-visible:ring-[#0F3D3E] focus-visible:border-[#0F3D3E] h-12 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Phone / WhatsApp
                </Label>
                <Input
                  id="phone"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  placeholder="+91 98XXXXXXXX"
                  className="bg-transparent border-[#0F3D3E]/15 focus-visible:ring-[#0F3D3E] focus-visible:border-[#0F3D3E] h-12 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Destination
                </Label>
                <Select
                  value={form.destination}
                  onValueChange={(v) => onChange("destination", v)}
                >
                  <SelectTrigger
                    data-testid="form-destination"
                    className="bg-transparent border-[#0F3D3E]/15 focus:ring-[#0F3D3E] h-12 rounded-xl text-[#0A0A0A]"
                  >
                    <SelectValue placeholder="Where to?" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {DESTINATIONS_LIST.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Travel dates
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="form-dates-trigger"
                      className="w-full h-12 rounded-xl border border-[#0F3D3E]/15 bg-transparent px-3 text-left text-sm flex items-center gap-3 hover:border-[#0F3D3E] transition-colors"
                    >
                      <CalendarDays size={18} className="text-[#2C7A7B]" />
                      <span className={travelDatesString() ? "text-[#0A0A0A]" : "text-[#4B5563]"}>
                        {travelDatesString() || "Select date range"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-white"
                    align="start"
                    data-testid="form-dates-popover"
                  >
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={1}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Budget per person
                </Label>
                <Select value={form.budget} onValueChange={(v) => onChange("budget", v)}>
                  <SelectTrigger
                    data-testid="form-budget"
                    className="bg-transparent border-[#0F3D3E]/15 focus:ring-[#0F3D3E] h-12 rounded-xl"
                  >
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {BUDGETS.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="travelers" className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Travellers
                </Label>
                <Input
                  id="travelers"
                  data-testid="form-travelers"
                  value={form.travelers}
                  onChange={(e) => onChange("travelers", e.target.value)}
                  placeholder="e.g. 2 adults, 1 child"
                  className="bg-transparent border-[#0F3D3E]/15 focus-visible:ring-[#0F3D3E] focus-visible:border-[#0F3D3E] h-12 rounded-xl"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Tell us more (optional)
                </Label>
                <Textarea
                  id="message"
                  data-testid="form-message"
                  value={form.message}
                  onChange={(e) => onChange("message", e.target.value)}
                  placeholder="Honeymoon, family trip, villa preference, dietary notes…"
                  rows={4}
                  className="bg-transparent border-[#0F3D3E]/15 focus-visible:ring-[#0F3D3E] focus-visible:border-[#0F3D3E] rounded-xl"
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <p className="text-xs text-[#4B5563] max-w-sm leading-relaxed">
                By submitting this form, you agree to be contacted by our travel
                team. We never share your details.
              </p>
              <button
                type="submit"
                data-testid="form-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-[#0F3D3E] text-[#F4EDE4] px-7 py-3.5 text-sm font-medium hover:bg-[#2C7A7B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Inquiry <Send size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </FadeIn>
      </div>
    </section>
  );
}
